import { idbGet, idbSet } from './indexedDbStorage';

const STORAGE_KEY = 'userwallet_nonce_cache';
const DEFAULT_TTL_MS = 3600000;

interface NonceEntry {
  n: string;
  t: number;
}

interface Stored {
  entries: NonceEntry[];
  ttlMs: number;
}

let cache: Map<string, number> = new Map();
let initialized = false;
let ttlMs = DEFAULT_TTL_MS;

function prune(now: number): void {
  for (const [n, t] of cache.entries()) {
    if (now - t >= ttlMs) {
      cache.delete(n);
    }
  }
}

async function load(): Promise<void> {
  const raw = await idbGet(STORAGE_KEY);
  cache = new Map();
  if (raw !== null) {
    try {
      const parsed = JSON.parse(raw) as Stored;
      ttlMs = parsed.ttlMs ?? DEFAULT_TTL_MS;
      for (const e of parsed.entries ?? []) {
        cache.set(e.n, e.t);
      }
    } catch {
      /* invalid JSON: start empty */
    }
  }
  prune(Date.now());
}

async function save(): Promise<void> {
  const entries: NonceEntry[] = [];
  for (const [n, t] of cache.entries()) {
    entries.push({ n, t });
  }
  const stored: Stored = { entries, ttlMs };
  await idbSet(STORAGE_KEY, JSON.stringify(stored));
}

/**
 * Anti-replay nonce store (IndexedDB-backed).
 * Call init() before hasUsed / markUsed.
 */
export async function init(ttlMsOverride?: number): Promise<void> {
  if (initialized) {
    return;
  }
  if (ttlMsOverride !== undefined) {
    ttlMs = ttlMsOverride;
  }
  await load();
  initialized = true;
}

/**
 * True if this nonce was already used within the TTL window.
 */
export function hasUsed(nonce: string): boolean {
  const t = cache.get(nonce);
  if (t === undefined) {
    return false;
  }
  if (Date.now() - t >= ttlMs) {
    cache.delete(nonce);
    return false;
  }
  return true;
}

/**
 * Mark nonce as used. Persists to IndexedDB.
 */
export async function markUsed(nonce: string, timestamp: number): Promise<void> {
  const now = Date.now();
  cache.set(nonce, timestamp);
  prune(now);
  await save();
}
